/**
 * A student's yes/no answers about themself, per chosen position: whether
 * they have completed the position's course (when the program asks it) and
 * whether they can attend its meeting times and the required training, and
 * commit its hours a week. The wizard holds them as { courses, commitments }, each mapping a position id
 * to a boolean (absent while unanswered); the server stores and returns a
 * list of { rule, position, met, minimum? }. They are self-assertions: no
 * GPA and no grade is ever collected.
 */

export const EMPTY_REQUIREMENT_ANSWERS = Object.freeze({
    courses: Object.freeze({}),
    commitments: Object.freeze({}),
});

/** "Have you completed MTH 1410 (or its equivalent)?" */
export const courseQuestion = (courseLabel) =>
    `Have you completed ${courseLabel} (or its equivalent)?`;

/** The statement a reviewer reads. */
export const courseStatement = (courseLabel) =>
    `Completed ${courseLabel} (or its equivalent)`;

/** What the student ticks: "I can attend these meeting times and the required training, and commit 10 hours a week." */
export const commitmentLabel = (hoursPerWeek) =>
    hoursPerWeek == null
        ? 'I can attend these meeting times and the required training, and commit the hours a week.'
        : `I can attend these meeting times and the required training, and commit ${hoursPerWeek} hours a week.`;

/** The statement a reviewer reads. */
export const commitmentStatement = (hoursPerWeek) =>
    hoursPerWeek
        ? `Confirmed they can attend the meeting times and the required training, and commit ${hoursPerWeek} hours a week`
        : 'Confirmed they can attend the meeting times and the required training, and commit the hours a week';

const boolOrNull = (value) => (typeof value === 'boolean' ? value : null);

/** The wizard's answers from a stored application's list. */
export function requirementAnswersFrom(stored) {
    const answers = { courses: {}, commitments: {} };
    for (const entry of stored || []) {
        if (!entry.position) continue;
        const position = String(entry.position);
        if (entry.rule === 'priorCompletion') {
            answers.courses[position] = boolOrNull(entry.met);
        } else if (entry.rule === 'positionCommitment') {
            answers.commitments[position] = entry.met === true;
        }
    }
    return answers;
}

/**
 * The list a save sends, for the positions listed: the course answers when
 * the program asks the question (`assertions`, the requirements endpoint's
 * studentAssertions) and each confirmed commitment. An unanswered question
 * is left out.
 */
export function requirementAttestationsBody(answers, assertions, positionIds) {
    const body = [];
    for (const position of positionIds) {
        const met = answers.courses[position];
        if (assertions?.priorCompletion && typeof met === 'boolean') {
            body.push({ rule: 'priorCompletion', position, met });
        }
        if (answers.commitments?.[position] === true) {
            body.push({ rule: 'positionCommitment', position, met: true });
        }
    }
    return body;
}

/** The questions still open, for the positions listed. */
export function unansweredRequirements(answers, assertions, positionIds) {
    const open = [];
    for (const position of positionIds) {
        if (answers.commitments?.[position] !== true) {
            open.push({ rule: 'positionCommitment', position });
        }
        if (
            assertions?.priorCompletion &&
            typeof answers.courses[position] !== 'boolean'
        ) {
            open.push({ rule: 'priorCompletion', position });
        }
    }
    return open;
}
